import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CKEditor } from 'ckeditor4-react';

const Admin = () => {
  const { id } = useParams(); // Detects if an active blog id parameter exists in the route mapping
  const navigate = useNavigate();
  const isEditMode = !!id;

  const MASTER_KEY = "axp1231";
  const [inputKey, setInputKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // DATA LAYER STRUCTURE
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    tag: 'Tech Stack',
    topImage: null,
    imagePreview: '',
    desc: ''
  });

  const [editorInstance, setEditorInstance] = useState(null);

  const handleAuthSubmit = (e) => {
    e.preventDefault();
    if (inputKey === MASTER_KEY) setIsAuthenticated(true);
  };

  // PULL DATA MANIFEST IF IN EDIT MODE
  useEffect(() => {
    if (!isAuthenticated || !isEditMode) return;

    const fetchBlogDataForEdit = async () => {
      try {
        const response = await fetch(`/api/blogs/details/${id}`);
        const result = await response.json();

        if (response.ok && result.success) {
          const blog = result.data;
          setFormData({
            title: blog.title || '',
            slug: blog.slug || '',
            tag: blog.tag || 'Tech Stack',
            topImage: null,
            imagePreview: blog.top_image || '', 
            desc: blog.desc || ''
          });

          // If the editor component frame is completely loaded up, sync data streams
          if (editorInstance) {
            editorInstance.setData(blog.desc || '');
          }
        } else {
          alert(`Sync Failure: ${result.message || 'Could not fetch record rows'}`);
        }
      } catch (err) {
        console.error('Failed to sync edit node records:', err);
      }
    };

    fetchBlogDataForEdit();
  }, [isAuthenticated, isEditMode, id, editorInstance]);

  // AUTO-GENERATING SLUG LOGIC
  const handleTitleChange = (e) => {
    const titleVal = e.target.value;
    const generatedSlug = titleVal
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') 
      .replace(/\s+/g, '-')         
      .replace(/-+/g, '-');         

    setFormData({
      ...formData,
      title: titleVal,
      slug: isEditMode ? formData.slug : generatedSlug // Leave unchanged in edit mode unless manually modified
    });
  };

  // IMAGE CAPTURE INTERCEPTOR
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        topImage: file,
        imagePreview: URL.createObjectURL(file) 
      });
    }
  };

  // SAVING / PUBLISHING MULTIPART DATA STREAM
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.desc.trim()) {
      alert("Please write some content in the editor before publishing.");
      return;
    }

    const dataPayload = new FormData();
    dataPayload.append('title', formData.title);
    dataPayload.append('slug', formData.slug);
    dataPayload.append('tag', formData.tag);
    dataPayload.append('desc', formData.desc); 

    if (formData.topImage) {
      dataPayload.append('topImage', formData.topImage);
    }

    // Route target dynamically based on state context
    const targetApiUrl = isEditMode ? `/api/blogs/update/${id}` : '/api/blogs/create';
    const requestMethod = isEditMode ? 'PUT' : 'POST';

    try {
      const response = await fetch(targetApiUrl, {
        method: requestMethod,
        body: dataPayload, 
      });

      const result = await response.json();

      if (response.ok) {
        alert(isEditMode ? '🎉 Blog post row records updated successfully!' : '🎉 Blog post published successfully!');
        navigate('/admin/blogs'); // Return safely back to our data engine dashboard table
      } else {
        alert(`Error: ${result.message || 'Failed to sync content logs'}`);
      }
    } catch (error) {
      console.error('API Stream Connection Error:', error);
      alert('Failed to connect to the backend server.');
    }
  };

  // Security Gate UI Layout - Light Frame Variant
  if (!isAuthenticated) {
    return (
      <div className="bg-slate-50 text-slate-800 min-h-screen flex items-center justify-center font-sans antialiased relative px-4">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-screen-2xl h-[400px] bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.05),transparent_50%)] pointer-events-none z-0" />
        
        <form onSubmit={handleAuthSubmit} className="bg-white p-10 rounded-3xl w-full max-w-md border border-slate-200/80 shadow-xl shadow-slate-200/40 text-center relative z-10">
          <div className="text-3xl mb-3">🔒</div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">MoreView Security Engine</h2>
          <p className="text-slate-400 text-xs mt-1.5 mb-8">Enter credentials to mount editing node.</p>
          <input
            type="password"
            required
            value={inputKey}
            onChange={(e) => setInputKey(e.target.value)}
            placeholder="Enter Key"
            className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 rounded-xl px-4 py-3.5 text-center text-sm text-slate-900 focus:outline-none transition-colors mb-5 font-medium"
          />
          <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold py-3.5 rounded-xl transition-all shadow-lg">
            Authenticate
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 text-slate-800 min-h-screen py-12 font-sans antialiased relative z-10">
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-12 lg:px-24">

        <form onSubmit={handleFormSubmit}>
          {/* HEADER DASHBOARD BAR */}
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-10 border-b border-slate-200 pb-8">
            <div>
              <h1 className="text-3xl font-black text-slate-900 tracking-tight">
                {isEditMode ? 'Modify Row Record' : 'Create New Blog Post'}
              </h1>
              <p className="text-sm text-slate-400 mt-1">Configure parameters and asset nodes for publication structures.</p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/blogs"
                className="text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 px-4 py-3 rounded-xl shadow-sm transition-all"
              >
                Cancel & Exit
              </Link>
              <button
                type="submit"
                className={`text-xs font-bold text-white px-5 py-3 rounded-xl shadow-lg transition-all flex items-center gap-1.5 ${
                  isEditMode 
                    ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-600/10 hover:shadow-amber-600/20' 
                    : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/10 hover:shadow-emerald-600/20'
                }`}
              >
                {isEditMode ? '💾 Save Changes' : '🚀 Publish Blog Post'}
              </button>
            </div>
          </div>

          {/* TOP STRUCTURAL META INPUTS LAYER */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-10 bg-white p-8 border border-slate-200 rounded-2xl shadow-xl shadow-slate-200/40">
            {/* Title Field */}
            <div className="md:col-span-6 flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Blog Title</label>
              <input
                type="text"
                required
                placeholder="e.g., Deep Dive into Automated Email Server Infrastructure"
                value={formData.title}
                onChange={handleTitleChange}
                className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 rounded-xl px-4 py-3.5 text-sm text-slate-900 focus:outline-none transition-colors font-medium"
              />
            </div>

            {/* Slug Field */}
            <div className="md:col-span-4 flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">URL Slug</label>
              <input
                type="text"
                required
                placeholder="automated-email-server-infrastructure"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 rounded-xl px-4 py-3.5 text-sm font-mono text-indigo-600 focus:outline-none transition-colors"
              />
            </div>

            {/* Classification Category Tag Dropdown */}
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Category Tag</label>
              <div className="relative">
                <select
                  value={formData.tag}
                  onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 rounded-xl px-4 py-3.5 text-sm text-slate-900 focus:outline-none transition-colors appearance-none cursor-pointer font-medium"
                >
                  <option value="Life Style">Life Style</option>
                  <option value="Weather Matrix">Weather Matrix</option>
                  <option value="Tech Stack">Tech Stack</option>
                  <option value="Market Updates">Market Updates</option>
                  <option value="Other">Other</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">▼</div>
              </div>
            </div>

            {/* Featured Banner Top Image Upload Node */}
            <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-center mt-2 pt-6 border-t border-slate-100">
              <div className="md:col-span-4 flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Featured Top Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="text-xs text-slate-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100 cursor-pointer"
                />
              </div>
              <div className="md:col-span-8">
                {formData.imagePreview && (
                  <div className="relative w-40 h-24 rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-100">
                    <img
                      src={formData.imagePreview}
                      alt="Banner Preview Layout"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* MAIN EDITOR AND WORKSPACE MATRIX */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

            {/* CKEDITOR 4 WRAPPER LAYER */}
            <div className="lg:col-span-7 bg-white rounded-2xl shadow-xl border border-slate-200/80 overflow-hidden p-2">
              <CKEditor
                editorUrl="https://cdn.ckeditor.com/4.22.1/standard/ckeditor.js"
                initData={formData.desc}
                config={{
                  toolbar: [
                    { name: 'document', items: [ 'Source', '-', 'Preview' ] },
                    { name: 'clipboard', items: [ 'Cut', 'Copy', 'Paste', 'PasteText', 'PasteFromWord', '-', 'Undo', 'Redo' ] },
                    { name: 'basicstyles', items: [ 'Bold', 'Italic', 'Underline', 'Strike', '-', 'RemoveFormat' ] },
                    '/',
                    { name: 'paragraph', items: [ 'NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock' ] },
                    { name: 'links', items: [ 'Link', 'Unlink' ] },
                    { name: 'insert', items: [ 'Image', 'Table', 'HorizontalRule' ] },
                    { name: 'styles', items: [ 'Format', 'Font', 'FontSize' ] },
                    { name: 'colors', items: [ 'TextColor', 'BGColor' ] }
                  ],
                  height: 480,
                  fullPage: true, 
                  autoParagraph: true,
                  fillEmptyBlocks: false,
                  enterMode: 1,
                  shiftEnterMode: 2,
                  allowedContent: true,
                  extraAllowedContent: '*(*);*{*}'
                }}
                onInstanceReady={(evt) => {
                  setEditorInstance(evt.editor);
                  const writer = evt.editor.dataProcessor.writer;

                  const docElements = ['html', 'head', 'body', 'title', 'style'];
                  docElements.forEach(tag => {
                    writer.setRules(tag, {
                      indent: false,
                      breakBeforeOpen: true,
                      breakAfterOpen: true,
                      breakBeforeClose: true,
                      breakAfterClose: true
                    });
                  });
                }}
                onChange={(evt) => {
                  const data = evt.editor.getData();
                  setFormData(prev => ({ ...prev, desc: data }));
                }}
              />
            </div>

            {/* REAL-TIME CONTENT AUDIT PREVIEW */}
            <div className="lg:col-span-5 bg-white border border-slate-200 p-6 rounded-2xl shadow-xl shadow-slate-200/40">
              <div className="flex justify-between items-center border-b border-slate-100 pb-4 mb-4">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Live Preview Channel</span>
                <span className="text-[10px] font-mono font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">HTML Output OK</span>
              </div>

              <div className="bg-slate-50 p-6 border border-slate-200 rounded-xl min-h-[525px] max-h-[600px] overflow-y-auto">
                <div
                  className="ck4-production-preview text-slate-700 prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.desc || '<p style="color: #94a3b8; font-family: sans-serif; font-size: 13px;">Awaiting HTML layout data streams from editor workspace node...</p>' }}
                />
              </div>
            </div>

          </div>
        </form>

      </div>
    </div>
  );
};

export default Admin;
